import type {CSSObject, Theme} from '@mui/material/styles';
import {HeroMode, HeroSize} from './types';

export const HERO_HEIGHT_VAR = '--hero-height';
export const HERO_OVERHANG_VAR = '--hero-overhang';

// Figma's hero banner heights, kept as the masthead size so a tenant that never
// opts into a viewport-sized hero renders exactly what it renders today.
const MASTHEAD_HEIGHT_MOBILE = 550;
const MASTHEAD_HEIGHT_DESKTOP = 881;

// Viewport-height units.
const FULL_HEIGHT = 110;
const VIEWPORT_HEIGHT = 100;
const COMPACT_HEIGHT = 70;

// The strip of hero the curtain already rests over before the page starts to
// scroll. It belongs to the pinned design, not to a height: a static hero has no
// curtain to hide anything behind, so it always overhangs by nothing.
const PINNED_OVERHANG = 10;
const NO_OVERHANG = 0;

const SVH_SUPPORTED = '@supports (height: 100svh)';

function viewportHeight(height: number): CSSObject {
  return {
    [HERO_HEIGHT_VAR]: `${height}vh`,
    [SVH_SUPPORTED]: {[HERO_HEIGHT_VAR]: `${height}svh`},
  };
}

const HERO_HEIGHT_STYLE: Record<HeroSize, (theme: Theme) => CSSObject> = {
  [HeroSize.Masthead]: theme => ({
    [HERO_HEIGHT_VAR]: `${MASTHEAD_HEIGHT_MOBILE}px`,
    [theme.breakpoints.up('md')]: {[HERO_HEIGHT_VAR]: `${MASTHEAD_HEIGHT_DESKTOP}px`},
  }),
  [HeroSize.Full]: () => viewportHeight(FULL_HEIGHT),
  [HeroSize.Viewport]: () => viewportHeight(VIEWPORT_HEIGHT),
  [HeroSize.Compact]: () => viewportHeight(COMPACT_HEIGHT),
};

function overhangStyle(mode: HeroMode): CSSObject {
  if (mode === HeroMode.Static) {
    return {[HERO_OVERHANG_VAR]: `${NO_OVERHANG}px`};
  }
  return {
    [HERO_OVERHANG_VAR]: `${PINNED_OVERHANG}vh`,
    [SVH_SUPPORTED]: {[HERO_OVERHANG_VAR]: `${PINNED_OVERHANG}svh`},
  };
}

// The frame both hero designs share: the size vars, the centred layout and the dark
// fallback behind the media so white content stays legible while it loads or if it
// fails (the media covers this once painted).
export function heroFrame(theme: Theme, mode: HeroMode, size: HeroSize): CSSObject {
  return {
    ...HERO_HEIGHT_STYLE[size](theme),
    ...overhangStyle(mode),
    isolation: 'isolate',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    background: theme.palette.colors.base.black,
  };
}

// What the hero costs the page: its height minus the strip the curtain rests over.
// The loading skeleton stands in for exactly this, so the page cannot jump when the
// real hero replaces it.
export function heroFlowHeight(theme: Theme, mode: HeroMode, size: HeroSize): CSSObject {
  return {
    ...HERO_HEIGHT_STYLE[size](theme),
    ...overhangStyle(mode),
    height: `calc(var(${HERO_HEIGHT_VAR}) - var(${HERO_OVERHANG_VAR}))`,
  };
}
